import { Plus } from "@untitledui/icons";
import type { FC, ReactElement, ReactNode } from "react";
import {
	EmptyState,
	EmptyStateType,
} from "@/components/Molecules/EmptyState/EmptyState";

export interface TableListEmptyStateProps {
	/** Empty-state title (e.g. "No Jobs Yet"). */
	title: ReactNode;
	/** Supporting description. */
	description?: ReactNode;
	/** Optional primary action label; when set with onAction, renders Add CTA. */
	actionLabel?: string;
	/** Called when the primary action is pressed. */
	onAction?: () => void;
	/** Leading icon for the action button. */
	actionIcon?: FC<{ className?: string }>;
	className?: string;
}

/**
 * TableView Empty state — design-system {@link EmptyState} with folder graphic
 * and optional Add CTA. Prefer this (or `EmptyState` directly) for zero-data tables.
 *
 * @see Figma TableView Empty 3106:14113
 * @see Figma EmptyState 622:6092
 */
export function TableListEmptyState({
	title,
	description,
	actionLabel,
	onAction,
	actionIcon = Plus,
	className,
}: TableListEmptyStateProps): ReactElement {
	const hasAction = Boolean(actionLabel && onAction);

	return (
		<EmptyState
			type={EmptyStateType.Default}
			title={title}
			description={description}
			showAction={hasAction}
			actionLabel={actionLabel}
			onAction={onAction}
			actionIcon={actionIcon}
			className={className}
			data-testid="table-list-empty-state"
		/>
	);
}
